import { formatInTimeZone } from 'date-fns-tz';
import { type Locale } from 'date-fns/locale';
import { enUS } from 'date-fns/locale/en-US';
import { enGB } from 'date-fns/locale/en-GB';
import { enAU } from 'date-fns/locale/en-AU';
import { enNZ } from 'date-fns/locale/en-NZ';
import { useContextualTimeZone } from '@/components/TimezoneProvider';
import { SimpleTooltip } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

// -- Locale resolution --

const LOCALE_MAP: Record<string, Locale> = {
  'en-US': enUS,
  'en-GB': enGB,
  'en-AU': enAU,
  'en-NZ': enNZ,
  // Fallback aliases
  en: enUS,
};

function getBrowserLocale(): Locale {
  const lang = navigator.language;
  return LOCALE_MAP[lang] ?? LOCALE_MAP[lang.split('-')[0]] ?? enUS;
}

// -- Format presets --

type TimePreset = 'date' | 'time' | 'datetime' | 'dateShort' | 'timeShort';

const FORMAT_PRESETS: Record<TimePreset, string> = {
  date: 'PP',         // "Apr 29, 2025"
  time: 'p',          // "3:45 PM"
  datetime: 'PPp',    // "Apr 29, 2025, 3:45 PM"
  dateShort: 'MMM d', // "Apr 29"
  timeShort: 'HH:mm', // "15:45"
};

function resolveFormat(format: TimePreset | string): string {
  return FORMAT_PRESETS[format as TimePreset] ?? format;
}

// -- Timezone helpers --

function getTimeZoneDisplayName(date: Date, timeZone: string): string {
  return (
    date
      .toLocaleString('en-US', { timeZoneName: 'long', timeZone })
      .split(',')
      .pop()
      ?.trim() ?? timeZone
  );
}

function getShortOffset(timeZone: string): string {
  return (
    new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'shortOffset',
    })
      .formatToParts(new Date())
      .find((p) => p.type === 'timeZoneName')?.value ?? ''
  );
}

function getTimeZoneAbbreviation(date: Date, timeZone: string): string {
  return (
    new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'short',
    })
      .formatToParts(date)
      .find((p) => p.type === 'timeZoneName')?.value ?? ''
  );
}

/** Returns true if the format preset or custom string includes a time component. */
function formatIncludesTime(format: TimePreset | string): boolean {
  if (format === 'date' || format === 'dateShort') return false;
  if (format === 'time' || format === 'datetime' || format === 'timeShort') return true;
  // For custom format strings, check for time-related tokens
  return /[hHmsa]/.test(format);
}

// -- Tooltip content --

function TimeTooltipContent({
  date,
  contextualTz,
  label,
  entityName,
}: {
  date: Date;
  contextualTz: string;
  label?: string;
  entityName?: string;
}) {
  const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const isSameTimezone = contextualTz === browserTz;
  const locale = getBrowserLocale();

  const contextualTime = formatInTimeZone(date, contextualTz, 'h:mm a', { locale });
  const contextualAbbr = getTimeZoneAbbreviation(date, contextualTz);
  const contextualDate = formatInTimeZone(date, contextualTz, 'PP', { locale });
  const contextualTzName = getTimeZoneDisplayName(date, contextualTz);
  const contextualOffset = getShortOffset(contextualTz);

  const browserTime = formatInTimeZone(date, browserTz, 'h:mm a', { locale });
  const browserAbbr = getTimeZoneAbbreviation(date, browserTz);
  const browserDate = formatInTimeZone(date, browserTz, 'PP', { locale });
  const browserTzName = getTimeZoneDisplayName(date, browserTz);
  const browserOffset = getShortOffset(browserTz);

  const heading = [label, entityName].filter(Boolean).join(' \u00b7 ') || 'Timezone';

  return (
    <div className="space-y-1.5 text-sm">
      <div>
        <p className="text-xs text-muted-foreground">{heading}</p>
        <p className="text-white">
          {contextualTime} {contextualAbbr} &middot; {contextualDate}
        </p>
        <p className="text-xs text-muted-foreground">
          {contextualTzName} ({contextualOffset})
        </p>
      </div>

      {!isSameTimezone && (
        <>
          <div className="border-t border-border/50" />
          <div>
            <p className="text-xs text-muted-foreground">Your timezone</p>
            <p className="text-white">
              {browserTime} {browserAbbr} &middot; {browserDate}
            </p>
            <p className="text-xs text-muted-foreground">
              {browserTzName} ({browserOffset})
            </p>
          </div>
        </>
      )}
    </div>
  );
}

// -- Main component --

interface TimeProps {
  /** The date to format. Accepts Date objects, ISO strings, or Unix timestamps. */
  date: Date | string | number | null | undefined;
  /**
   * Format preset or date-fns format string.
   * Presets: 'date' (PP), 'time' (p), 'datetime' (PPp), 'dateShort' (MMM d), 'timeShort' (HH:mm)
   * @default 'datetime'
   */
  format?: TimePreset | string;
  /** Override the contextual timezone. When provided, ignores TimezoneProvider. */
  timeZone?: string;
  /** CSS class for the rendered `<time>` element. */
  className?: string;
  /** Disable the comparison tooltip. @default false */
  noTooltip?: boolean;
  /** Fallback text when date is null/undefined/invalid. @default '' */
  fallback?: string;
}

export function Time({
  date,
  format = 'datetime',
  timeZone: timeZoneOverride,
  className,
  noTooltip = false,
  fallback = '',
}: TimeProps) {
  const ctx = useContextualTimeZone();

  if (date == null) return <>{fallback}</>;

  const dateObj = date instanceof Date ? date : new Date(date);
  if (isNaN(dateObj.getTime())) return <>{fallback}</>;

  const contextualTz = timeZoneOverride ?? ctx.timeZone;
  const formatStr = resolveFormat(format);
  const locale = getBrowserLocale();

  const formattedTime = formatInTimeZone(dateObj, contextualTz, formatStr, { locale });
  const abbreviation = formatIncludesTime(format) ? getTimeZoneAbbreviation(dateObj, contextualTz) : '';
  const displayText = abbreviation ? `${formattedTime} ${abbreviation}` : formattedTime;
  const isoDatetime = dateObj.toISOString();

  if (noTooltip) {
    return (
      <time dateTime={isoDatetime} className={cn(className)}>
        {displayText}
      </time>
    );
  }

  return (
    <SimpleTooltip
      content={
        <TimeTooltipContent
          date={dateObj}
          contextualTz={contextualTz}
          label={timeZoneOverride ? undefined : ctx.label}
          entityName={timeZoneOverride ? undefined : ctx.entityName}
        />
      }
    >
      <time
        dateTime={isoDatetime}
        className={cn(
          'cursor-help underline decoration-dotted decoration-muted-foreground/50 underline-offset-2 hover:decoration-muted-foreground',
          className
        )}
      >
        {displayText}
      </time>
    </SimpleTooltip>
  );
}
